import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface SaveLayoutViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Whether the operator can flip the "Share with team" flag —
   *  gated on settings.project.edit upstream. */
  canShare: boolean;
  /** Resolves to the new view on success, or null on failure
   *  (toast already shown). The dialog closes itself on
   *  success. */
  onSubmit: (params: {
    name: string;
    is_shared: boolean;
  }) => Promise<{ id: number } | null>;
}

/**
 * Tiny "Save layout view" dialog: name + optional share checkbox,
 * then calls the parent's onSubmit. The actual layout payload is
 * collected by the parent when this dialog opens, so the
 * dialog stays focused on UX.
 */
export function SaveLayoutViewDialog({
  open,
  onOpenChange,
  canShare,
  onSubmit,
}: SaveLayoutViewDialogProps) {
  const [name, setName] = useState("");
  const [shareWithTeam, setShareWithTeam] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Reset on each open so a previous save doesn't leak its name.
  useEffect(() => {
    if (open) {
      setName("");
      setShareWithTeam(false);
      setSubmitting(false);
    }
  }, [open]);

  async function submit() {
    const trimmed = name.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    const result = await onSubmit({
      name: trimmed,
      is_shared: shareWithTeam && canShare,
    });
    setSubmitting(false);
    if (result) onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Save layout view</DialogTitle>
          <DialogDescription>
            Name this layout so you (and optionally your team) can pick it
            from the dropdown later.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-600">Name</label>
            <Input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Sales team view"
              onKeyDown={(e) => {
                if (e.key === "Enter") void submit();
              }}
            />
          </div>
          {canShare && (
            <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
              <Checkbox
                checked={shareWithTeam}
                onCheckedChange={(v) => setShareWithTeam(!!v)}
              />
              <span>Share with team</span>
            </label>
          )}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => void submit()}
            disabled={submitting || name.trim().length === 0}
          >
            {submitting && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            Save view
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
